import { Sparkles, ShoppingBag, Lightbulb, PenLine, Clapperboard, Video, ClipboardCheck } from "lucide-react";

export const MISSIONS = [
  { id: 1, num: "01", title: "Je découvre l'UGC", pageTitle: "Comprendre l'UGC", short: "Ce qu'est une vidéo UGC, et pourquoi tu peux commencer dès aujourd'hui.", icon: Sparkles },
  { id: 2, num: "02", title: "Je choisis mon produit", pageTitle: "Choisir son produit", short: "Un objet de ton quotidien, facile à montrer et à filmer.", icon: ShoppingBag },
  { id: 3, num: "03", title: "Je trouve mon idée", pageTitle: "Trouver son concept", short: "Un angle, une situation, un bénéfice : ton idée de vidéo.", icon: Lightbulb },
  { id: 4, num: "04", title: "J'écris mon script", pageTitle: "Écrire son script", short: "Accroche, démonstration, conclusion : un texte naturel.", icon: PenLine },
  { id: 5, num: "05", title: "Je prépare mon tournage", pageTitle: "Préparer son tournage", short: "Ton storyboard en 4 plans et ta checklist de préparation.", icon: Clapperboard },
  { id: 6, num: "06", title: "Je réalise ma vidéo", pageTitle: "Réaliser sa vidéo", short: "Filmer, assembler et exporter, étape par étape.", icon: Video },
  { id: 7, num: "07", title: "Je vérifie ma création", pageTitle: "Vérifier sa vidéo", short: "Ton auto-évaluation et ton bilan de première créatrice.", icon: ClipboardCheck },
];

export const CONTENT = {
  1: {
    objective: "Comprendre ce qu'est une vidéo UGC et ce qui la distingue de l'influence.",
    sections: [
      {
        title: "Qu'est-ce que l'UGC ?",
        text: [
          "UGC signifie « User Generated Content », c'est-à-dire du contenu créé par une utilisatrice plutôt que par une agence de publicité.",
          "Concrètement, une créatrice UGC réalise une courte vidéo, le plus souvent verticale, qui montre un produit de façon naturelle : à quoi il sert, comment on l'utilise, ce que l'on remarque en le découvrant.",
        ],
      },
      {
        title: "UGC ou influence : quelle différence ?",
        list: [
          { t: "L'influence", d: "L'influenceuse publie le contenu sur son propre compte. La marque s'intéresse surtout à son audience et à sa communauté." },
          { t: "L'UGC", d: "La créatrice réalise une vidéo et la remet à la marque, qui la diffuse sur ses propres supports : réseaux sociaux, site, publicités. Le nombre d'abonnés n'est pas le critère principal." },
        ],
      },
      {
        title: "Pourquoi les marques utilisent ces contenus",
        list: [
          { t: "Un ton naturel", d: "Une vidéo tournée chez soi ressemble aux contenus que l'on regarde déjà sur son téléphone." },
          { t: "Le produit en situation", d: "On voit l'objet dans la vraie vie, pas seulement en photo sur fond blanc." },
          { t: "Un format adapté", d: "Court, vertical, pensé pour être regardé sur smartphone." },
          { t: "Plusieurs angles possibles", d: "Une marque peut présenter un même produit de différentes façons, avec plusieurs vidéos." },
        ],
      },
      {
        title: "Les principaux formats de vidéos UGC",
        list: [
          { t: "Démonstration", d: "Tu montres comment le produit fonctionne, étape par étape." },
          { t: "Découverte", d: "Tu ouvres le produit et tu partages ta première prise en main." },
          { t: "Routine", d: "Tu montres le produit intégré à un moment de ta journée." },
          { t: "Tutoriel", d: "Tu expliques une astuce ou une façon précise de l'utiliser." },
          { t: "Questions-réponses", d: "Tu réponds à une question que l'on se pose souvent sur ce type de produit." },
        ],
      },
      {
        title: "Ton smartphone suffit",
        text: [
          "Pour t'entraîner, tu as seulement besoin de ton téléphone, d'une fenêtre pour la lumière et d'une application de montage gratuite.",
          "La qualité d'une vidéo UGC vient surtout de la clarté de l'idée, de la lumière et de la stabilité de l'image — pas du prix du matériel.",
        ],
      },
    ],
    example: {
      title: "Trois vidéos UGC simples",
      items: [
        { t: "Démonstration beauté", d: "Face à une fenêtre, tu appliques un baume à lèvres : gros plan sur la texture, puis sur le rendu, avec une phrase qui explique quand tu l'utilises." },
        { t: "Découverte d'un accessoire", d: "Tu sors une pince à cheveux de sa pochette, tu montres sa forme, puis comment elle se fixe en quelques secondes." },
        { t: "Objet du quotidien", d: "Tu remplis une gourde, tu la fermes, tu la retournes pour montrer qu'elle ne fuit pas, puis tu la glisses dans ton sac." },
      ],
    },
    success: "Tu sais maintenant ce qu'est l'UGC et ce qui le distingue de l'influence. Prochaine étape : choisir le produit de ta première vidéo.",
  },
  2: {
    objective: "Choisir un produit facile à filmer pour ta vidéo d'entraînement.",
    sections: [
      {
        title: "Les 4 critères d'un bon produit d'entraînement",
        list: [
          { t: "Disponible chez toi", d: "Tu l'as sous la main : tu peux filmer quand tu veux et refaire des prises sans rien acheter." },
          { t: "Facile à utiliser", d: "Son utilisation se comprend en quelques secondes, sans mode d'emploi." },
          { t: "Facile à montrer", d: "Il est assez grand pour être visible à l'écran, et son action se voit : une texture, une ouverture, un rangement…" },
          { t: "Une utilité concrète", d: "Il répond à un besoin simple que tout le monde comprend tout de suite." },
        ],
      },
      {
        title: "À éviter pour une première vidéo",
        list: [
          { t: "Les effets invisibles", d: "Un produit dont l'effet n'apparaît qu'après plusieurs semaines est difficile à montrer." },
          { t: "Les objets minuscules ou très brillants", d: "Ils sont compliqués à filmer nettement avec un smartphone." },
          { t: "Les produits que tu ne connais pas", d: "Choisis un objet que tu as déjà utilisé, pour parler avec justesse." },
        ],
      },
    ],
    example: {
      title: "L'exemple de Léa",
      items: [
        { t: "Son choix", d: "Léa choisit sa gourde isotherme. Elle l'utilise tous les jours, la prise en main est évidente et le bouchon à visser est un détail facile à filmer en gros plan." },
      ],
    },
    success: "Ton produit est choisi. Tu as maintenant la matière de ta vidéo : passons à l'idée que tu vas raconter.",
  },
  3: {
    objective: "Transformer ton produit en une idée de vidéo claire.",
    sections: [
      {
        title: "Un concept, c'est quoi ?",
        text: [
          "Un concept répond à une seule question : qu'est-ce que je raconte avec ce produit ?",
          "Il réunit trois ingrédients : un produit, une situation et un bénéfice concret. Une vidéo = une idée principale.",
        ],
      },
      {
        title: "Trois angles pour commencer",
        list: [
          { t: "Démonstration", d: "Tu montres comment le produit fonctionne, étape par étape. Idéal quand son utilisation se voit bien." },
          { t: "Découverte", d: "Tu filmes ta prise en main : tu ouvres, tu observes, tu montres ce que tu remarques. Idéal pour un accessoire." },
          { t: "Routine", d: "Tu intègres le produit à un moment de ta journée : le matin, avant le sport, au bureau. Idéal pour un objet du quotidien." },
        ],
      },
      {
        title: "La règle d'or",
        text: [
          "Le bénéfice doit être concret et visible à l'écran. « Elle se glisse dans mon sac » se montre. « Elle va changer ta vie » ne se montre pas.",
        ],
      },
    ],
    example: {
      title: "Le concept de Léa — angle Routine",
      items: [
        { t: "Pour qui", d: "Les personnes qui bougent beaucoup dans la journée." },
        { t: "Situation", d: "Je prépare mon sac le matin avant de partir." },
        { t: "Ce que je montre", d: "Je remplis la gourde, je la ferme, je la glisse dans mon sac." },
        { t: "Bénéfice concret", d: "Elle se transporte facilement et le bouchon ferme bien." },
        { t: "Idée principale", d: "Ma gourde me suit toute la journée." },
      ],
    },
    success: "Ton concept est posé. Tu sais quoi montrer et pourquoi : il est temps de l'écrire.",
  },
  4: {
    objective: "Construire un script naturel et efficace, en trois parties.",
    sections: [
      {
        title: "La structure en 3 temps",
        list: [
          { t: "Accroche", d: "Une phrase qui attire l'attention dès les premières secondes. Elle annonce ce que l'on va voir." },
          { t: "Démonstration", d: "Tu présentes le produit et tu montres comment tu l'utilises. Décris simplement ce que tu fais." },
          { t: "Conclusion", d: "L'idée à retenir, en une phrase. Elle reprend ton idée principale." },
        ],
      },
      {
        title: "Écrire comme tu parles",
        list: [
          { t: "Des phrases courtes", d: "Une idée par phrase. C'est plus facile à dire et à comprendre." },
          { t: "Lis à voix haute", d: "Si une phrase te fait trébucher, simplifie-la." },
          { t: "Reste vraie", d: "Ne dis que ce que tu constates toi-même. N'invente ni bénéfice, ni résultat, ni expérience personnelle." },
        ],
      },
      {
        title: "Quelle longueur ?",
        text: ["Pour une première vidéo, vise entre 15 et 45 secondes, soit environ 40 à 110 mots. L'indicateur sous ton script t'aide à te situer."],
      },
    ],
    example: {
      title: "Le script de Léa",
      items: [
        { t: "Accroche", d: "« Je te montre comment j'utilise ma gourde pendant toute ma journée. »" },
        { t: "Démonstration", d: "« Le matin, je la remplis d'eau. Je visse le bouchon, je la retourne : rien ne coule. Ensuite, je la glisse dans mon sac grâce à sa poignée. »" },
        { t: "Conclusion", d: "« Une gourde simple, qui me suit partout. »" },
      ],
    },
    success: "Ton script est prêt. Tu sais exactement quoi dire : place à la préparation du tournage.",
  },
  5: {
    objective: "Organiser tes plans avant de filmer, pour tourner sereinement.",
    sections: [
      {
        title: "Les 6 bases d'un bon tournage",
        list: [
          { t: "Lumière naturelle", d: "Place-toi face à une fenêtre, en journée. Évite le soleil direct et les lumières de plafond qui créent des ombres." },
          { t: "Cadrage", d: "Garde le produit au centre et assez grand. Laisse un peu d'espace autour, sans couper l'objet." },
          { t: "Stabilité", d: "Pose ton téléphone contre un support stable (livres, trépied, verre). Garde les coudes près du corps si tu filmes à la main." },
          { t: "Vidéo verticale", d: "Tiens ton téléphone à la verticale : c'est le format attendu pour les vidéos UGC." },
          { t: "Arrière-plan", d: "Choisis un fond simple et rangé. Retire les objets qui détournent l'attention du produit." },
          { t: "Son", d: "Filme dans une pièce calme, coupe les appareils bruyants et parle à 30–50 cm du téléphone." },
        ],
      },
    ],
    example: {
      title: "La préparation de Léa",
      items: [
        { t: "Le lieu", d: "Le plan de travail de sa cuisine, dégagé, à côté de la fenêtre, en milieu de matinée." },
        { t: "Le matériel", d: "Son téléphone posé contre une pile de livres, objectif nettoyé avec un tissu doux." },
        { t: "Les plans", d: "Elle a noté ses 4 plans sur le storyboard avant de commencer à filmer." },
      ],
    },
    success: "Ta préparation est terminée. Tu as un plan clair pour chaque séquence : tu peux filmer quand tu veux.",
  },
  6: {
    objective: "Filmer et assembler ta première vidéo UGC d'entraînement.",
    sections: [
      {
        title: "Avant de commencer",
        text: [
          "Tu as ton script et ton storyboard : il ne reste plus qu'à filmer et assembler. Pas besoin de montage complexe — quelques coupes bien placées suffisent.",
        ],
      },
      {
        title: "Ton outil de montage",
        text: [
          "Tu peux utiliser l'application de ton choix. CapCut est un exemple d'application gratuite très utilisée ; l'éditeur vidéo intégré à ton téléphone peut aussi suffire.",
          "UGC GO ne se connecte à aucune application et n'héberge pas ta vidéo : tu la montes à part, elle reste sur ton appareil, et tu reviens ici cocher tes étapes.",
        ],
      },
    ],
    example: {
      title: "Le tournage de Léa",
      items: [
        { t: "Les prises", d: "Elle filme chacun de ses 4 plans deux ou trois fois, puis garde la prise la plus nette." },
        { t: "Le montage", d: "Elle coupe les hésitations au début et à la fin de chaque clip, ajoute un court texte à l'écran, vérifie le son et exporte en vertical." },
      ],
    },
    success: "Ta vidéo est prête et enregistrée sur ton appareil. Dernière étape : la regarder avec un œil bienveillant et précis.",
  },
  7: {
    objective: "Évaluer ta première création pour savoir quoi garder et quoi améliorer.",
    sections: [
      {
        title: "Pourquoi s'auto-évaluer ?",
        text: [
          "Regarder ta vidéo avec une grille simple t'aide à repérer ce qui fonctionne déjà et ce que tu peux améliorer la prochaine fois. Ce n'est pas une note : c'est un point d'étape.",
        ],
      },
      {
        title: "Comment regarder ta vidéo",
        list: [
          { t: "Sur ton téléphone", d: "Regarde-la en plein écran vertical, comme quelqu'un qui la découvrirait." },
          { t: "Deux fois", d: "Une première fois sans le son pour juger l'image, une seconde fois avec le son." },
          { t: "Avec bienveillance", d: "Une première vidéo n'a pas besoin d'être parfaite. Note ce qui est réussi avant ce qui est à revoir." },
        ],
      },
    ],
    example: {
      title: "Le bilan de Léa",
      items: [
        { t: "Point fort", d: "« Le produit est bien visible et la lumière est belle. »" },
        { t: "Amélioration", d: "« Mon accroche est un peu lente, je la raccourcirai. »" },
        { t: "Ce que j'ai appris", d: "« Préparer mes plans avant m'a fait gagner beaucoup de temps. »" },
      ],
    },
    success: "Ton auto-évaluation est terminée.",
  },
};

export const QUIZ = [
  {
    id: "q1",
    question: "Faut-il avoir beaucoup d'abonnés pour créer des contenus UGC ?",
    options: ["Oui, au moins 10 000 abonnés", "Non, l'audience n'est pas le critère principal", "Oui, sinon les marques refusent toujours"],
    correct: 1,
    explanation: "En UGC, c'est la vidéo qui compte, pas ton audience : la marque diffuse généralement le contenu sur ses propres supports.",
  },
  {
    id: "q2",
    question: "Peut-on s'entraîner avec un produit que l'on possède ?",
    options: ["Oui, c'est même idéal pour commencer", "Non, il faut un produit envoyé par une marque"],
    correct: 0,
    explanation: "Un produit que tu connais et que tu as sous la main est parfait : tu peux filmer à ton rythme et parler avec justesse.",
  },
  {
    id: "q3",
    question: "Une vidéo UGC doit-elle forcément être publiée sur son compte ?",
    options: ["Oui, toujours", "Non, elle peut rester sur ton téléphone ou être remise à une marque"],
    correct: 1,
    explanation: "Une vidéo UGC n'a pas besoin d'être publiée sur ton compte. Ta vidéo d'entraînement peut simplement rester sur ton appareil.",
  },
];

export const CATEGORIES = ["Beauté & soin", "Accessoire", "Mode", "Maison", "Cuisine", "Sport & bien-être", "Papeterie & bureau", "Objet tech du quotidien", "Autre"];

export const PRODUCT_EXAMPLE = {
  name: "Gourde isotherme 500 ml",
  category: "Sport & bien-être",
  why: "Je l'utilise tous les jours et elle est facile à prendre en main devant la caméra.",
  utility: "Transporter une boisson dans mon sac pendant la journée.",
  detail: "Le bouchon qui se visse et la poignée de transport.",
};

export const ANGLES = [
  { id: "demonstration", label: "Démonstration", desc: "Je montre comment il fonctionne, étape par étape." },
  { id: "decouverte", label: "Découverte", desc: "Je filme ma prise en main et ce que je remarque." },
  { id: "routine", label: "Routine", desc: "Je l'intègre à un moment de ma journée." },
];

export const HOOKS = ["Je te montre comment j'utilise… ", "Voilà ce que j'aime dans… ", "Si tu cherches une solution pour…, regarde ça. "];

export const STORYBOARD = [
  { id: "p1", title: "Plan 1 — Présentation", action: "Je montre le produit.", explain: "Le produit apparaît en entier, bien éclairé, pour que l'on comprenne tout de suite de quoi parle la vidéo.", example: "La gourde posée sur le plan de travail, puis prise en main face caméra." },
  { id: "p2", title: "Plan 2 — Utilisation", action: "Je montre comment il fonctionne.", explain: "On voit le geste principal : ouvrir, appliquer, remplir, ranger… C'est le cœur de ta démonstration.", example: "Je remplis la gourde au robinet et je visse le bouchon." },
  { id: "p3", title: "Plan 3 — Détail", action: "Je filme une caractéristique intéressante.", explain: "Un gros plan sur le détail que tu as choisi en mission 2. Rapproche-toi doucement, sans perdre la netteté.", example: "Gros plan sur le bouchon et la poignée de transport." },
  { id: "p4", title: "Plan 4 — Conclusion", action: "Je termine ma vidéo.", explain: "Un plan simple qui accompagne ta phrase de conclusion et clôt la vidéo.", example: "Je glisse la gourde dans mon sac et je ferme la fermeture éclair." },
];

export const PREP_CHECKLIST = [
  { id: "battery", label: "Téléphone chargé", desc: "Et assez d'espace de stockage pour plusieurs prises." },
  { id: "lens", label: "Objectif propre", desc: "Un coup de tissu doux sur la caméra arrière et avant." },
  { id: "light", label: "Lumière vérifiée", desc: "Face à une fenêtre, sans soleil direct ni contre-jour." },
  { id: "frame", label: "Cadrage stable", desc: "Téléphone posé ou calé, à la verticale." },
  { id: "background", label: "Arrière-plan adapté", desc: "Fond simple, rangé, sans objet qui distrait." },
  { id: "plans", label: "Plans préparés", desc: "Tes 4 plans sont notés dans le storyboard." },
];

export const MAKING_STEPS = [
  { id: "s1", label: "Filmer les plans", desc: "Filme chacun de tes 4 plans, en vertical. Fais 2 ou 3 prises par plan." },
  { id: "s2", label: "Regarder les séquences", desc: "Visionne tes clips : la lumière, la netteté et le cadrage te conviennent-ils ?" },
  { id: "s3", label: "Refaire les prises nécessaires", desc: "Refilme uniquement les plans flous, sombres ou mal cadrés." },
  { id: "s4", label: "Ouvrir une application de montage", desc: "Par exemple CapCut, ou l'éditeur vidéo de ton téléphone." },
  { id: "s5", label: "Importer les clips", desc: "Ajoute tes meilleures prises dans l'ordre du storyboard." },
  { id: "s6", label: "Couper les passages inutiles", desc: "Supprime les hésitations, les silences et les mouvements au début et à la fin de chaque clip." },
  { id: "s7", label: "Ajouter les textes ou la voix off si utile", desc: "Un texte court et lisible, ou ta voix qui lit ton script. Ce n'est pas obligatoire." },
  { id: "s8", label: "Vérifier le son", desc: "Écoute toute la vidéo : la voix est-elle claire, sans bruit gênant ?" },
  { id: "s9", label: "Exporter la vidéo", desc: "Enregistre la vidéo finale sur ton téléphone, en vertical." },
];

export const EVAL_CRITERIA = [
  { id: "visible", label: "Le produit est visible" },
  { id: "idea", label: "L'idée principale est compréhensible" },
  { id: "bright", label: "Les plans sont suffisamment lumineux" },
  { id: "frame", label: "Le cadrage est adapté" },
  { id: "edit", label: "Le montage est cohérent" },
  { id: "text", label: "Le texte est lisible", optional: true, hint: "si utilisé" },
  { id: "sound", label: "Le son est compréhensible", optional: true, hint: "si utilisé" },
];

export const EVAL_CHOICES = [
  { id: "yes", label: "Oui" },
  { id: "improve", label: "À améliorer" },
  { id: "na", label: "Non applicable", optionalOnly: true },
];
